/**
 * TransactionType Service
 *
 * Axios-based API client for TransactionType entity with authentication,
 * error handling, and optional caching support.
 *
 * Generated by CodeGen OS v1.0.0
 * Generated at: 2026-04-25T14:23:22.147323Z
 */

// Vite environment type augmentation
declare global {
  interface ImportMeta {
    env: {
      VITE_API_BASE_URL?: string;
      [key: string]: string | undefined;
    };
  }
}

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type {
  TransactionType,
  TransactionTypeCreateRequest,
  TransactionTypeUpdateRequest,
  TransactionTypeListResponse,
  TransactionTypeFilterOptions,
} from '../types/transaction_type.types';

// ---- CODAIC:CUSTOM IMPORTS ----
// Add custom imports below. This section is preserved across regeneration.
// ---- CODAIC:END CUSTOM ----

// API Base URL - configurable via environment variable
// Empty string default means requests use relative paths (going through Vite proxy in dev)
const API_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
  '';

// Tenant ID - required by multi-tenant backend
const TENANT_ID =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TENANT_ID) ||
  (typeof localStorage !== 'undefined' && localStorage.getItem('tenant_id')) ||
  '00000000-0000-0000-0000-000000000000';

// Default cache TTL in milliseconds (5 minutes)
const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;

// Resource path — single source of truth for this entity's API prefix.
// Use this in CODAIC:CUSTOM methods: `${RESOURCE_PATH}/my-action`
const RESOURCE_PATH = '/api/v1/transaction-types';

/**
 * TransactionType service error class for typed error handling
 */
export class TransactionTypeServiceError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errorCode?: string
  ) {
    super(message);
    this.name = 'TransactionTypeServiceError';
  }
}

/**
 * Cache entry structure for in-memory caching
 */
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * TransactionType Service Class
 *
 * Provides CRUD operations for TransactionType entities with:
 * - Bearer token authentication
 * - Typed error handling
 * - Optional in-memory caching
 * - Filter support
 */
class TransactionTypeService {
  private client: AxiosInstance;
  private cache: Map<string, CacheEntry<TransactionType[]>>;
  private cacheTTL: number;

  constructor(baseURL?: string, cacheTTL: number = DEFAULT_CACHE_TTL_MS) {
    this.client = axios.create({
      baseURL: baseURL || API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.cache = new Map();
    this.cacheTTL = cacheTTL;

    // Add request interceptor for authentication and tenant context
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token =
          typeof localStorage !== 'undefined'
            ? localStorage.getItem('access_token')
            : null;

        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Always send tenant ID header (required by multi-tenant backend)
        if (config.headers) {
          const tenantId =
            (typeof localStorage !== 'undefined' && localStorage.getItem('tenant_id')) ||
            TENANT_ID;
          config.headers['X-Tenant-ID'] = tenantId;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          console.warn('[TransactionTypeService] Unauthorized — clearing session');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_expiry');
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(entry: CacheEntry<TransactionType[]> | undefined): boolean {
    if (!entry) return false;
    const now = Date.now();
    return now - entry.timestamp < entry.ttl;
  }

  /**
   * Get cached data if valid
   */
  private getCached(key: string): TransactionType[] | null {
    const entry = this.cache.get(key);
    if (this.isCacheValid(entry)) {
      console.debug(`[TransactionTypeService] Cache hit for key: ${key}`);
      return entry!.data;
    }
    return null;
  }

  /**
   * Set cache entry
   */
  private setCache(key: string, data: TransactionType[]): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: this.cacheTTL,
    });
    console.debug(
      `[TransactionTypeService] Cached ${data.length} items for key: ${key}`
    );
  }

  /**
   * Handle API errors and transform to typed error
   */
  private handleError(error: unknown): TransactionTypeServiceError {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{
        detail?: string;
        message?: string;
        error_code?: string;
      }>;
      const message =
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        'An error occurred';
      const statusCode = axiosError.response?.status;
      const errorCode = axiosError.response?.data?.error_code;
      return new TransactionTypeServiceError(message, statusCode, errorCode);
    }
    return new TransactionTypeServiceError(
      error instanceof Error ? error.message : 'Unknown error'
    );
  }

  /**
   * Fetch all transaction_types with optional filters
   *
   * @param filters - Optional filter options
   * @param forceRefresh - Skip cache and fetch fresh data
   * @returns Array of TransactionType objects
   * @throws TransactionTypeServiceError on fetch failure
   */
  async getAll(
    filters?: TransactionTypeFilterOptions,
    forceRefresh: boolean = false
  ): Promise<TransactionType[]> {
    const cacheKey = `all_transaction_types_${JSON.stringify(filters || {})}`;

    // Check cache first
    if (!forceRefresh) {
      const cached = this.getCached(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const url = `${RESOURCE_PATH}/`;
    try {
      console.log(`[TransactionTypeService] GET ${url}`, { params: filters });
      const response = await this.client.get<
        TransactionType[] | TransactionTypeListResponse
      >(url, {
        params: { page_size: 200, ...(filters as Record<string, unknown> | undefined) },
      });

      // Handle both array response and wrapped response
      const data = response.data;
      const items = Array.isArray(data)
        ? data
        : (data as TransactionTypeListResponse).items ||
          (data as TransactionTypeListResponse).transaction_types ||
          [];

      console.log(`[TransactionTypeService] GET ${url} → ${response.status} OK`, {
        count: items.length,
        firstItem: items[0] ? { id: items[0].id } : null,
      });

      this.setCache(cacheKey, items);
      return items;
    } catch (error) {
      const serviceError = this.handleError(error);
      console.error(`[TransactionTypeService] GET ${url} → FAILED`, {
        status: serviceError.statusCode,
        message: serviceError.message,
        errorCode: serviceError.errorCode,
      });
      throw serviceError;
    }
  }

  /**
   * Get a single transaction_type by ID
   *
   * @param id - TransactionType ID (UUID)
   * @returns TransactionType object
   * @throws TransactionTypeServiceError on fetch failure
   */
  async getById(id: string): Promise<TransactionType> {
    const url = `${RESOURCE_PATH}/${id}`;
    try {
      console.log(`[TransactionTypeService] GET ${url}`);
      const response = await this.client.get<TransactionType>(url);
      console.log(`[TransactionTypeService] GET ${url} → ${response.status} OK`, {
        id: response.data.id,
      });
      return response.data;
    } catch (error) {
      const serviceError = this.handleError(error);
      console.error(`[TransactionTypeService] GET ${url} → FAILED`, {
        status: serviceError.statusCode,
        message: serviceError.message,
        errorCode: serviceError.errorCode,
      });
      throw serviceError;
    }
  }

  /**
   * Create a new transaction_type
   *
   * @param data - TransactionType creation data
   * @returns Created TransactionType object
   * @throws TransactionTypeServiceError on creation failure
   */
  async create(data: TransactionTypeCreateRequest): Promise<TransactionType> {
    const url = `${RESOURCE_PATH}/`;
    try {
      console.log(`[TransactionTypeService] POST ${url}`, { data });
      const response = await this.client.post<TransactionType>(url, data);
      console.log(`[TransactionTypeService] POST ${url} → ${response.status} Created`, {
        id: response.data.id,
      });
      // Invalidate cache on successful mutation
      this.clearCache();
      return response.data;
    } catch (error) {
      const serviceError = this.handleError(error);
      console.error(`[TransactionTypeService] POST ${url} → FAILED`, {
        status: serviceError.statusCode,
        message: serviceError.message,
        errorCode: serviceError.errorCode,
      });
      throw serviceError;
    }
  }

  /**
   * Update an existing transaction_type
   *
   * @param id - TransactionType ID (UUID)
   * @param data - Partial TransactionType update data
   * @returns Updated TransactionType object
   * @throws TransactionTypeServiceError on update failure
   */
  async update(
    id: string,
    data: TransactionTypeUpdateRequest
  ): Promise<TransactionType> {
    const url = `${RESOURCE_PATH}/${id}`;
    try {
      console.log(`[TransactionTypeService] PUT ${url}`, { data });
      const response = await this.client.put<TransactionType>(url, data);
      console.log(`[TransactionTypeService] PUT ${url} → ${response.status} OK`, {
        id: response.data.id,
      });
      // Invalidate cache on successful mutation
      this.clearCache();
      return response.data;
    } catch (error) {
      const serviceError = this.handleError(error);
      console.error(`[TransactionTypeService] PUT ${url} → FAILED`, {
        status: serviceError.statusCode,
        message: serviceError.message,
        errorCode: serviceError.errorCode,
      });
      throw serviceError;
    }
  }

  /**
   * Delete a transaction_type
   *
   * @param id - TransactionType ID (UUID)
   * @throws TransactionTypeServiceError on deletion failure
   */
  async delete(id: string): Promise<void> {
    const url = `${RESOURCE_PATH}/${id}`;
    try {
      console.log(`[TransactionTypeService] DELETE ${url}`);
      const response = await this.client.delete(url);
      console.log(`[TransactionTypeService] DELETE ${url} → ${response.status} OK`, {
        deletedId: id,
      });
      // Invalidate cache on successful mutation
      this.clearCache();
    } catch (error) {
      const serviceError = this.handleError(error);
      console.error(`[TransactionTypeService] DELETE ${url} → FAILED`, {
        status: serviceError.statusCode,
        message: serviceError.message,
        errorCode: serviceError.errorCode,
      });
      throw serviceError;
    }
  }

  /**
   * Get active transaction_types only
   *
   * @param forceRefresh - Skip cache and fetch fresh data
   * @returns Array of active TransactionType objects
   */
  async getActive(forceRefresh: boolean = false): Promise<TransactionType[]> {
    const all = await this.getAll(undefined, forceRefresh);
    return all.filter((item) => (item as any).is_active !== false);
  }

  /**
   * Search transaction_types by query string
   *
   * @param query - Search query string
   * @param forceRefresh - Skip cache and fetch fresh data
   * @returns Array of matching TransactionType objects
   */
  async search(
    query: string,
    forceRefresh: boolean = false
  ): Promise<TransactionType[]> {
    return this.getAll({ search: query } as TransactionTypeFilterOptions, forceRefresh);
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.cache.clear();
    console.debug('[TransactionTypeService] Cache cleared');
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { entries: number; keys: string[] } {
    return {
      entries: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  /**
   * Set cache TTL (time-to-live)
   *
   * @param ttlMs - TTL in milliseconds
   */
  setCacheTTL(ttlMs: number): void {
    this.cacheTTL = ttlMs;
  }

  /**
   * Format transaction_type for dropdown display
   *
   * @param item - TransactionType object
   * @returns Formatted string for display
   */
  formatForDropdown(item: TransactionType): string {
    // Use code-name pattern if available, otherwise just name or id
    const code = (item as any).code;
    const name = (item as any).name;

    if (code && name) {
      return `${code} - ${name}`;
    }
    if (name) {
      return name;
    }
    return item.id;
  }

  /**
   * Get transaction_types formatted for dropdown selection
   *
   * @param forceRefresh - Skip cache and fetch fresh data
   * @returns Array of { value, label, data } objects for dropdown
   */
  async getForDropdown(
    forceRefresh: boolean = false
  ): Promise<Array<{ value: string; label: string; data: TransactionType }>> {
    const items = await this.getActive(forceRefresh);
    return items.map((item) => ({
      value: item.id,
      label: this.formatForDropdown(item),
      data: item,
    }));
  }

  // ---- CODAIC:CUSTOM METHODS ----
  // Add custom service methods below. This section is preserved across regeneration.
  // Use RESOURCE_PATH for URLs: e.g. `${RESOURCE_PATH}/my-action`
  // ---- CODAIC:END CUSTOM ----
}

// Export singleton instance for convenience
export const transaction_typeService = new TransactionTypeService();

// Export the class for testing and custom instantiation
export { TransactionTypeService };

// Default export for convenience
export default transaction_typeService;
